import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import ActiveRequest from "./pages/ActiveRequest";
import Appointments from "./pages/Appointments";
import Profile from "./pages/Profile";
import Resources from "./pages/Resources";
import ResponderApplication from "./pages/ResponderApplication";
import AdminDashboard from "./pages/AdminDashboard";
import ResponderDirectory from "./pages/ResponderDirectory";
import SafetyAndEthics from "./pages/SafetyAndEthics";
import ContactUs from "./pages/ContactUs";
import VolunteerTraining from "./pages/VolunteerTraining";
import QuickHelp from "./pages/QuickHelp";
import ResponderProfile from "./pages/ResponderProfile";
import RequestHistory from "./pages/RequestHistory";
import ResponderAnalytics from "./pages/ResponderAnalytics";
import Leaderboard from "./pages/Leaderboard";
import ChatHistory from "./pages/ChatHistory";
import ChatTranscript from "./pages/ChatTranscript";
import ResponderBadges from "./pages/ResponderBadges";
import SMSPreferences from "./pages/SMSPreferences";
import AdminFeedbackAnalytics from "./pages/AdminFeedbackAnalytics";
import AdminEscalationTracking from "./pages/AdminEscalationTracking";
import AvailabilityScheduler from "./pages/AvailabilityScheduler";
import PeerSupportChat from "./pages/PeerSupportChat";
import VeteranFollowUp from "./pages/VeteranFollowUp";
import CrisisResources from "./pages/CrisisResources";
import FamilyResources from "./pages/FamilyResources";
import MoodTracking from "./pages/MoodTracking";
import AnonymousHelp from "./pages/AnonymousHelp";
import SupportGroups from "./pages/SupportGroups";
import AdminBulkAlerts from "./pages/AdminBulkAlerts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

      if (!sessionId) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        // Clear hash and navigate to dashboard with user data
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/");
      }
    };

    checkAuth();
  }, [navigate, location.state, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Clone children with user prop
  return typeof children === 'function' ? children({ user, setUser }) : children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/request/:requestId"
        element={
          <ProtectedRoute>
            {({ user }) => <ActiveRequest user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            {({ user }) => <Appointments user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Profile user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            {({ user }) => <Resources user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/apply-responder"
        element={
          <ProtectedRoute>
            {({ user }) => <ResponderApplication user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminDashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminFeedbackAnalytics user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/escalations"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminEscalationTracking user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            {({ user }) => <AvailabilityScheduler user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/peer-support"
        element={
          <ProtectedRoute>
            {({ user }) => <PeerSupportChat user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/follow-ups"
        element={
          <ProtectedRoute>
            {({ user }) => <VeteranFollowUp user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/crisis-resources"
        element={
          <ProtectedRoute>
            {({ user }) => <CrisisResources user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/family-resources"
        element={<FamilyResources />}
      />
      <Route
        path="/mood-tracking"
        element={
          <ProtectedRoute>
            {({ user }) => <MoodTracking user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/anonymous-help"
        element={<AnonymousHelp />}
      />
      <Route
        path="/support-groups"
        element={
          <ProtectedRoute>
            {({ user }) => <SupportGroups user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bulk-alerts"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminBulkAlerts user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/responders"
        element={
          <ProtectedRoute>
            {({ user }) => <ResponderDirectory user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/safety-and-ethics" element={<SafetyAndEthics />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/quick-help" element={<QuickHelp />} />
      <Route
        path="/responder/:userId"
        element={
          <ProtectedRoute>
            {({ user }) => <ResponderProfile user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/volunteer-training"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <VolunteerTraining user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/request-history"
        element={
          <ProtectedRoute>
            {({ user }) => <RequestHistory user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            {({ user }) => <ResponderAnalytics user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route
        path="/chat-history"
        element={
          <ProtectedRoute>
            {({ user }) => <ChatHistory user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:requestId"
        element={
          <ProtectedRoute>
            {({ user }) => <ChatTranscript user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/badges"
        element={
          <ProtectedRoute>
            {({ user }) => <ResponderBadges user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sms-preferences"
        element={
          <ProtectedRoute>
            {({ user }) => <SMSPreferences user={user} />}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" richColors />
        <PWAInstallPrompt />
      </BrowserRouter>
    </div>
  );
}

export default App;
